import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Shield, Users, Heart, CheckCircle, X } from 'lucide-react';

const WhyValenRockPage = () => {
  const principles = [
    {
      id: 1,
      icon: Target,
      title: "Single Point of Accountability",
      description: "One trusted partner manages everything — no contractor juggling, no conflicting advice."
    },
    {
      id: 2,
      icon: Shield,
      title: "Independent & Honest",
      description: "We're advisors who tell you what matters vs. what's noise — not contractors looking for work."
    },
    {
      id: 3,
      icon: Heart,
      title: "Quality Over Volume",
      description: "Limited clients means everyone gets proper attention. That's why we run a waitlist."
    },
    {
      id: 4,
      icon: Users,
      title: "Long-Term Focus",
      description: "Your Home Profile grows with you. We plan for years ahead, not just today's crisis."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden mt-20">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Why Homeowners
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Choose Valenrock<sup className="text-[0.5em]">™</sup></span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-6 max-w-3xl mx-auto leading-relaxed">
            Most homeowners get an inspection report and a PDF full of problems — then nothing. No priorities. No plan. No one to call.
          </p>
          
          {/* Visual Hook Badge */}
          <div className="bg-white/10 backdrop-blur-sm border border-[#CFB973]/30 rounded-full px-6 py-3 inline-flex items-center gap-3">
            <span className="text-[#CFB973] text-lg">✦</span>
            <span className="text-white/90 text-sm md:text-base italic">
              We don't just tell you what's wrong. <span className="text-[#CFB973] font-semibold">We tell you what matters.</span>
            </span>
            <span className="text-[#CFB973] text-lg">✦</span>
          </div>
        </div>
      </section>

      {/* Two Divisions Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#003366] mb-3">
              Two Divisions, One Standard
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Maintenance or renovation — same commitment to quality, transparency, and long-term relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <div className="mb-3">
                  <Shield className="text-[#003366] w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Valenrock™ Home Services</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Whole-Home Assessments</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Maintenance planning</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Priority scheduling</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Vendor coordination</span>
                  </li>
                </ul>
                <Link to="/assessment">
                  <Button className="mt-5 bg-[#003366] hover:bg-[#003366]/90 text-white text-sm">
                    Learn About Assessments
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <div className="mb-3">
                  <Heart className="text-[#003366] w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Valenrock<sup className="text-[0.5em]">™</sup> Remodelers</h3>
                <ul className="space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Kitchen & bathroom renovations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Custom carpentry</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Basement finishing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#CFB973] mr-2 mt-0.5 flex-shrink-0" size={16} />
                    <span>Full home renovations</span>
                  </li>
                </ul>
                <Link to="/remodeling">
                  <Button className="mt-5 bg-[#003366] hover:bg-[#003366]/90 text-white text-sm">
                    View Remodeling Services
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Home Profile Section */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-[#003366] mb-3">
            Your Home Profile: A Living Document
          </h2>
          <p className="text-gray-600 mb-4">
            An inspection report is frozen in time. Your Home Profile evolves — updated whenever you choose future work with us. It's not just what's wrong today. It's a roadmap for years ahead.
          </p>
          <Link to="/assessment">
            <Button className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-semibold">
              Learn About Assessments
            </Button>
          </Link>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle) => {
              const Icon = principle.icon;
              return (
                <Card key={principle.id} className="border-2 border-gray-100 hover:border-[#CFB973] transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#003366]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-[#003366]" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#003366] mb-2">{principle.title}</h3>
                        <p className="text-gray-600">{principle.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Valenrock™ Is For */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#003366] mb-3">
              Is Valenrock™ Right for You?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're not for everyone — and that's intentional. Here's how to know if we're a fit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* For */}
            <Card className="border-2 border-green-200 bg-green-50/30">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <h3 className="text-lg font-bold text-[#003366]">Good Fit</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You want fewer surprises, not just cheaper fixes</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You'd rather plan than react to emergencies</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You value one trusted relationship over ten random ones</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You treat your home like an asset worth protecting</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Not For */}
            <Card className="border-2 border-red-200 bg-red-50/30">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                    <X className="text-white" size={16} />
                  </div>
                  <h3 className="text-lg font-bold text-[#003366]">Not the Best Fit</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start space-x-2">
                    <X className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You only call when something breaks</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <X className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You shop for the lowest bid every time</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <X className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You enjoy vetting and managing contractors yourself</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <X className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700">You prefer one-off fixes over a long-term plan</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#003366] mb-4">Our Credentials</h2>
          <p className="text-gray-600 mb-10">Licensed, insured, and certified to protect your home and family.</p>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 mb-10">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm">Licensed & Insured</p>
              <p className="text-gray-500 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Heart className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm">100% Satisfaction</p>
              <p className="text-gray-500 text-xs">Guarantee</p>
            </div>
          </div>
          
          {/* EPA Certification - Standalone */}
          <div className="flex flex-col items-center pt-8 border-t border-[#003366]/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_smarthouse-staging/artifacts/inesgu3y_IMG_7280.png" 
              alt="EPA Lead-Safe Certified Firm" 
              className="h-28 md:h-32 w-auto object-contain mb-3 mix-blend-multiply"
            />
            <p className="text-[#003366] font-bold">EPA Lead-Safe Certified Firm</p>
            <p className="text-gray-500 text-sm">Certification #NAT-F326598-1</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyValenRockPage;
